
import numbro from 'numbro'
import { mapActions, mapGetters } from 'vuex'
import CreateEkrm from '@/components/ekrm/CreateEkrm.vue'
import NgtorchRunChip from '@/components/NgtorchRunChip.vue'
import _ from 'lodash'
import { isComposerEkrm } from '@/utils/ekrmOrigin.js'

// The key CreateEkrm.vue writes its unsaved draft under. Named here so the prompt this view puts up
// and the wizard that answers it cannot drift apart.
const EKRM_DRAFT_KEY = 'ekrmDraft'

export default {
  data () {
    return {
      editItem: null,
      cloning: false,
      draft: null,
      currentTab: 0,
      createEkrmDialog: false,
      createEkrmFromDraftDialog: false,
      deleteDialog: false,
      deleteItem: null,
      maxNameLength: v => v.length <= 20 || this.$t('views.manageEkrm.inputTooLong'),
      snack: false,
      snackColor: '',
      snackText: '',
      search: ''
    }
  },
  methods: {
    ...mapActions('manager', [
      'loadEkrmModels',
      'deleteEkrmModel',
      'trashEkrmModel',
      'renameEkrmModel'
    ]),
    ...mapActions('storage', [
      'getFileDownloadUrl'
    ]),
    openWizard () {
      this.createEkrmFromDraftDialog = false
      this.createEkrmDialog = true
    },
    continueDraft () {
      this.openWizard()
    },
    startFromScratch () {
      localStorage.removeItem(EKRM_DRAFT_KEY)
      this.draft = null
      this.openWizard()
    },
    createEkrmMethod () {
      this.editItem = null
      this.cloning = false
      this.draft = localStorage.getItem(EKRM_DRAFT_KEY)
      if (this.draft) {
        this.createEkrmFromDraftDialog = true
      } else {
        this.createEkrmDialog = true
      }
    },
    triggerDeleteEkrmModel () {
      if (this.deleteItem) {
        // A built EKRM goes to the trash. One still being built has nothing to restore and is
        // deleted for good.
        const deleted = this.deleteItem.config
          ? this.trashEkrmModel({ ekrm_id: this.deleteItem.id })
          : this.deleteEkrmModel({ ekrm_id: this.deleteItem.id })
        deleted.then(() => {
          this.snack = true
          this.snackColor = 'success'
          this.snackText = this.$t('views.manageEkrm.snackEkrmDeleted')
          this.closeDeleteDialog()
        }).catch(error => {
          console.error(error)
          this.snack = true
          this.snackColor = 'error'
          this.snackText = this.$t('views.manageEkrm.snackErrorDeletingEkrm')
          this.closeDeleteDialog()
        })
      }
    },
    closeDeleteDialog () {
      this.deleteDialog = false
      this.deleteItem = null
    },
    confirmDelete () {
      if (this.deleteItem) {
        this.triggerDeleteEkrmModel()
      }
    },
    formatValue: function (value) {
      return numbro(value).format({
        average: true,
        lowPrecision: false,
        mantissa: 1,
        optionalMantissa: true
      })
    },
    // countries is loaded by App.vue when the session comes back, while this table starts drawing
    // as soon as the ekrm snapshot lands: two independent round trips, so on a page reload the
    // rows can be rendered before the country list exists. Reading .name off the missing match
    // threw inside the render and took the whole table down with it. Falling back to the raw id
    // also covers a market that is no longer in settings/countries.
    marketName (item) {
      const market = item && item.definition && item.definition.reference ? item.definition.reference.market : null
      if (!market) {
        return ''
      }
      const match = this.countries.filter(country => country.id === market)[0]
      return match ? match.name : market
    },
    // Firestore Timestamps carry a valueOf() that returns a STRING, so subtracting two of them
    // yielded NaN and left this column in whatever order the snapshot happened to arrive in.
    // Compare milliseconds, with the same date_edited-wins rule the column displays.
    sortDate (item) {
      const date = item.date_edited ? item.date_edited : item.date_created
      if (date && typeof date.toMillis === 'function') {
        return date.toMillis()
      }
      if (date instanceof Date) {
        return date.getTime()
      }
      return 0
    },
    // lodash's default only fills in for undefined, so a field stored explicitly as null reached
    // toString() and threw. Everything missing sorts as an empty string instead.
    sortText (item, path) {
      const value = _.get(item, path)
      return value === null || value === undefined ? '' : value.toString()
    },
    dateModified (item) {
      const dateModified = item.date_edited ? item.date_edited : item.date_created
      if (dateModified) {
        return dateModified.toDate().toString().slice(4, 24)
      } else {
        return null
      }
    },
    customSort: function (items, sortBy, sortDesc, locale, customSorters) {
      const sortCol = sortBy[0]
      if (sortCol === 'date_created') {
        if (sortDesc[0]) {
          items.sort((a, b) => this.sortDate(a) - this.sortDate(b))
        } else {
          items.sort((a, b) => this.sortDate(b) - this.sortDate(a))
        }
      } else {
        if (!sortDesc[0]) {
          items.sort((a, b) => {
            return this.sortText(b, sortCol).localeCompare(this.sortText(a, sortCol), undefined, {
              numeric: true,
              sensitivity: 'base'
            })
          })
        } else {
          items.sort((a, b) => {
            return this.sortText(a, sortCol).localeCompare(this.sortText(b, sortCol), undefined, {
              numeric: true,
              sensitivity: 'base'
            })
          })
        }
      }
      return items
    },
    saveName (item) {
      console.log(item)
      if (item && item.definition.reference.category) {
        const name = item.definition.reference.category.trim()
        item.definition.reference.category = this.$t('views.manageEkrm.updating')
        this.renameEkrmModel({
          id: item.id,
          name: name
        }).then(() => {
          this.snack = true
          this.snackColor = 'success'
          this.snackText = this.$t('views.manageEkrm.snackEkrmRenamed')
        }).catch(error => {
          // Reload EKRM models, to get rid of (non)updated names
          this.loadEkrmModels()
          console.log(error)
          this.snack = true
          this.snackColor = 'error'
          this.snackText = this.$t('views.manageEkrm.snackErrorRenamingEkrm')
        })
      }
    },
    viewJSON: function (item) {
      console.log(JSON.stringify(item))
    },
    downloadEkrmPkl: async function (item) {
      if (!item || !item.id) return

      try {
        const pklPath = `models/ekrm/${item.id}.pkl`
        const downloadUrl = await this.getFileDownloadUrl({
          path: pklPath
        })

        // Create temporary link and trigger download
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = `${item.definition.reference.category}.pkl`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        this.snack = true
        this.snackColor = 'success'
        this.snackText = this.$t('views.manageEkrm.snackPklDownloadStarted')
      } catch (error) {
        console.error('Error downloading EKRM PKL:', error)
        this.snack = true
        this.snackColor = 'error'
        this.snackText = this.$t('views.manageEkrm.snackErrorDownloadingPkl')
      }
    }
  },
  computed: {
    ...mapGetters('auth', [
      'authUser'
    ]),
    ...mapGetters('manager', [
      'ekrmModels'
    ]),
    ...mapGetters('settings', [
      'countries'
    ]),
    // The Model Composer's EKRMs are listed by the composer, next to the models built from them
    visibleEkrmModels () {
      return this.ekrmModels.filter(model => !isComposerEkrm(model))
    },
    ekrmHeaders () {
      return [
        { text: this.$t('views.manageEkrm.headerName'), value: 'definition.reference.category' },
        { text: this.$t('views.manageEkrm.headerMarket'), value: 'definition.reference.market' },
        { text: this.$t('views.manageEkrm.headerCreatedOn'), value: 'date_created' },
        { text: this.$t('views.manageEkrm.headerCreatedBy'), value: 'created_by' },
        { text: this.$t('views.manageEkrm.headerActions'), value: 'action', sortable: false }
      ]
    }
  },
  components: {
    CreateEkrm,
    NgtorchRunChip
  },
  created: function () {
    this.loadEkrmModels()
  }
}
